// CC Sessions — operator-facing read API.
//
// Lists Pattern A (corvuscode project mode) sessions per customer and
// renders the supervisor audit trail per drilldown. Read-only for now;
// mutation routes (abort, force-fire) live in the bearer-authed
// agent_cc surface and are not wired here yet.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

export type CCSessionsListResp = components["schemas"]["CCSessionsList"];
export type CCSessionSummary = components["schemas"]["CCSessionSummary"];
export type CCSessionDetail = components["schemas"]["CCSessionDetail"];
export type CCSupervisorRunSummary =
  components["schemas"]["CCSupervisorRunSummary"];

export function useCCSessionsList(customerId: string | null) {
  return useQuery({
    queryKey: ["cc-sessions", customerId],
    enabled: customerId !== null && customerId !== "",
    queryFn: ({ signal }) =>
      apiFetch<CCSessionsListResp>(
        `/api/customers/${customerId}/cc/sessions`,
        { signal },
      ),
    // Sessions are slow-moving (supervisor fires every 15 min by default).
    // 10s polling is enough to feel live without hammering.
    staleTime: 8_000,
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });
}

export type CCDeploysListResp = components["schemas"]["CCDeploysList"];
export type CCDeploySummary = components["schemas"]["CCDeploySummary"];

export function useCCDeploysList(
  customerId: string | null,
  opts: { liveOnly?: boolean; refetchInterval?: number } = {},
) {
  const liveOnly = opts.liveOnly ?? true;
  return useQuery({
    queryKey: ["cc-deploys", customerId, liveOnly],
    enabled: customerId !== null && customerId !== "",
    queryFn: ({ signal }) =>
      apiFetch<CCDeploysListResp>(
        `/api/customers/${customerId}/cc/deploys?live_only=${liveOnly}`,
        { signal },
      ),
    staleTime: 8_000,
    refetchInterval: opts.refetchInterval ?? 10_000,
    refetchOnWindowFocus: true,
  });
}

export interface CCSessionTranscript {
  session_id: string;
  session_status: string;
  spec_summary: string | null;
  spec_body: string | null;
  agent_template: string;
  workspace_root: string | null;
  workspace_files: Array<{ path: string; size: number; mtime: string }>;
  daemon_session_id: string | null;
  saved_at: string | null;
  supervisor_fires: number;
  transcript_status: "ready" | "no_transcript_yet";
  transcript_note: string | null;
  messages: Array<{
    role: string;
    blocks?: Array<Record<string, unknown>>;
    [k: string]: unknown;
  }>;
  file_path: string;
}

export function useCCSessionTranscript(
  customerId: string | null,
  sessionId: string | null,
  liveOff = false,
) {
  return useQuery({
    queryKey: ["cc-session-transcript", customerId, sessionId],
    enabled:
      customerId !== null && customerId !== "" &&
      sessionId !== null && sessionId !== "",
    queryFn: ({ signal }) =>
      apiFetch<CCSessionTranscript>(
        `/api/customers/${customerId}/cc/sessions/${sessionId}/transcript`,
        { signal },
      ),
    staleTime: 5_000,
    // While the modal's SSE stream is live the poll is frozen — the stream
    // is the source of truth; on SSE failure the modal flips this back on.
    refetchInterval: liveOff ? false : 8_000,
  });
}

/** Live turn-event row from the SSE stream (cc_events). */
export interface CCTurnEvent {
  id: number;
  kind: string;
  role: string | null;
  blocks: unknown[];
  meta: Record<string, unknown>;
  created_at: string;
}

export type CCSessionSummaryLive =
  components["schemas"]["CCSessionSummary"] & {
    last_event_at?: string | null;
  };

export type CCOneshotsListResp = components["schemas"]["CCOneshotsList"];
export type CCOneshotSummary = components["schemas"]["CCOneshotSummary"];

export function useCCOneshotsList(customerId: string | null) {
  return useQuery({
    queryKey: ["cc-oneshots", customerId],
    enabled: customerId !== null && customerId !== "",
    queryFn: ({ signal }) =>
      apiFetch<CCOneshotsListResp>(
        `/api/customers/${customerId}/cc/oneshots`,
        { signal },
      ),
    staleTime: 8_000,
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });
}

export function useDeleteSession(customerId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      apiFetch<{ session_id: string; rows_removed: boolean; workspace: string }>(
        `/api/customers/${customerId}/cc/sessions/${sessionId}?purge_workspace=true`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cc-sessions", customerId] });
    },
  });
}

export function useDeleteOneshot(customerId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (oneshotId: string) =>
      apiFetch<{ oneshot_id: string; rows_removed: boolean; workspace: string }>(
        `/api/customers/${customerId}/cc/oneshots/${oneshotId}?purge_workspace=true`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cc-oneshots", customerId] });
    },
  });
}

export function useTeardownDeploy(customerId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch<{ slug: string; killed: number; nginx_status: string }>(
        `/api/customers/${customerId}/cc/deploys/${slug}/teardown`,
        { method: "POST" },
      ),
    onSuccess: () => {
      // Invalidate every variant of the deploys query (live_only=true
      // for the gallery, live_only=false if anything else fetches it).
      void qc.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === "cc-deploys",
      });
    },
  });
}

export interface CCDeployLog {
  deploy_id: string;
  path: string;
  bytes: number;
  content: string;
}

export function useCCDeployLog(
  customerId: string | null,
  deployId: string | null,
) {
  return useQuery({
    queryKey: ["cc-deploy-log", customerId, deployId],
    enabled:
      customerId !== null && customerId !== "" &&
      deployId !== null && deployId !== "",
    queryFn: ({ signal }) =>
      apiFetch<CCDeployLog>(
        `/api/customers/${customerId}/cc/deploys/${deployId}/log`,
        { signal },
      ),
    staleTime: 30_000,
  });
}

export function useCCSessionDetail(
  customerId: string | null,
  sessionId: string | null,
) {
  return useQuery({
    queryKey: ["cc-session", customerId, sessionId],
    enabled:
      customerId !== null && customerId !== "" &&
      sessionId !== null && sessionId !== "",
    queryFn: ({ signal }) =>
      apiFetch<CCSessionDetail>(
        `/api/customers/${customerId}/cc/sessions/${sessionId}`,
        { signal },
      ),
    staleTime: 8_000,
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });
}
